import { type ComponentChildren } from 'preact';
import { useRef } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, LogoutIcon, MenuIcon, MoonIcon, MoreHorizontalIcon, PlusIcon, RefreshIcon, SearchIcon, SettingsIcon, SunIcon } from '@xantory/icons';
import { useChromePopover } from '../../hooks/use-chrome-popover';
import { styles } from './top-bar.styles';
import { useT, useLocaleStore } from '@xantory/i18n';

// Endonyms on purpose — a language picker names each language in itself.
const LANGUAGES = [{
  code: 'de',
  name: 'Deutsch'
}, {
  code: 'en',
  name: 'English'
}] as const;
export interface TopBarUser {
  name: string;
  role: string;
  initials: string;
}

/** One signed-in account in the switcher (R-303). */
export interface TopBarAccount extends TopBarUser {
  id: string;
  active: boolean;
}
interface TopBarProps {
  /** [section, page] — e.g. ['IoT Devices', 'Microcontrollers']. */
  breadcrumb: readonly [string, string];
  onHamburger: () => void;
  /** Sidebar is a rail (R-346) — rotates the arrow so it always points at what
   *  pressing it will do. */
  sidebarCollapsed?: boolean | undefined;
  /** True below the tablet breakpoint: the control opens an off-canvas DRAWER
   *  rather than folding the sidebar in place. The arrow is the same either way
   *  (user, 2026-09-16) — what changes is which state counts as "hidden". */
  drawerMode?: boolean | undefined;
  /** Drawer visibility, used only in `drawerMode` to aim the arrow. */
  drawerOpen?: boolean | undefined;
  /** Opens the ⌘K palette (R-346). Absent ⇒ no trigger, the same convention
   *  `onRefresh` uses: the console must not offer a control the app layer has
   *  not wired. The shortcut itself is the app layer's, not ours —
   *  a keydown listener owned by a component that may be unmounted on a mobile
   *  route would be a shortcut that silently stops working. */
  onOpenSearch?: (() => void) | undefined;
  /** Rendered inside the trigger; `⌘K` on a Mac, `Ctrl K` elsewhere. */
  searchShortcutLabel?: string | undefined;
  /** Slot before the icon cluster — the cloud site switcher. */
  actions?: ComponentChildren;
  /** The notification bell (wired by the app layer; absent = no bell). */
  bell?: ComponentChildren;
  theme: 'light' | 'dark';
  onThemeToggle: () => void;
  /** R-239: force a data refresh. Absent ⇒ no button (Storybook, tests).
   *
   *  Cached reads are the right default for a 24/7 dashboard, but they mean the
   *  operator sometimes knows something changed before the console does — after a
   *  device is remapped, a batch generated, or a colleague edits the same record.
   *  A reload also works, but it re-downloads the shell and loses scroll position;
   *  this invalidates the query cache and nothing else. */
  onRefresh?: (() => void) | undefined;
  /** True while any query is in flight — spins the icon, so the button reports
   *  what it did instead of the operator pressing it again. */
  refreshing?: boolean | undefined;
  /** Signed-in user — shown as the rightmost avatar with a Settings/Logout menu. */
  user?: TopBarUser | undefined;
  settingsHref?: string | undefined;
  onLogout?: (() => void) | undefined;
  /** Every signed-in account (R-303). Absent or of length ≤ 1 ⇒ the switcher
   *  section is not rendered at all — one account needs no chooser. */
  accounts?: readonly TopBarAccount[] | undefined;
  onSwitchAccount?: ((id: string) => void) | undefined;
  onSignOutAccount?: ((id: string) => void) | undefined;
  /** Absent ⇒ no "Add account" row, so the console cannot offer a flow the app
   *  layer has not wired (no decorative UI). */
  onAddAccount?: (() => void) | undefined;
}
export function TopBar({
  breadcrumb,
  onHamburger,
  sidebarCollapsed,
  drawerMode,
  drawerOpen,
  onOpenSearch,
  searchShortcutLabel,
  actions,
  bell,
  theme,
  onThemeToggle,
  onRefresh,
  refreshing,
  user,
  settingsHref = '/settings',
  onLogout,
  accounts,
  onSwitchAccount,
  onSignOutAccount,
  onAddAccount
}: TopBarProps) {
  const t = useT('ui');
  const locale = useLocaleStore(s => s.locale);
  const setLocale = useLocaleStore(s => s.setLocale);
  const [section, page] = breadcrumb;
  const langBtnRef = useRef<HTMLButtonElement>(null);
  const userBtnRef = useRef<HTMLButtonElement>(null);
  const moreBtnRef = useRef<HTMLButtonElement>(null);

  // One store for every dropdown in the chrome — the bell and the site switcher
  // arrive through slots and join the same one, which is what makes "open this,
  // close that" possible at all. See `useChromePopover`.
  // §24 — these panels scroll on a phone; none of them shows a scrollbar.
  const menuProps = stylex.props(styles.menu);
  const menuCls = [menuProps.className, 'scroll-hidden'].filter(Boolean).join(' ');
  const wideProps = stylex.props(styles.menu, styles.menuWide);
  const wideCls = [wideProps.className, 'scroll-hidden'].filter(Boolean).join(' ');
  const lang = useChromePopover(langBtnRef);
  const userMenu = useChromePopover(userBtnRef);
  const more = useChromePopover(moreBtnRef);
  return <header {...stylex.props(styles.bar)}>
      <button type="button" aria-label={t('toggleNav')} aria-expanded={drawerMode ? !!drawerOpen : !sidebarCollapsed} onClick={onHamburger} {...stylex.props(styles.collapseBtn)}>
        {/* Three horizontal lines (user, 2026-09-16). ⓘ It does NOT rotate: a
            chevron could point at what pressing it does, a symmetric glyph
            cannot, so the state is carried by `aria-expanded` alone. */}
        <MenuIcon size={16} strokeWidth={2.2} />
      </button>

      <nav aria-label={t('breadcrumb')} {...stylex.props(styles.crumb, styles.crumbCompact)}>
        {section && <>
            <span>{section}</span>
            <span aria-hidden="true" {...stylex.props(styles.crumbSep)}>
              <ChevronRightIcon size={13} strokeWidth={2.2} />
            </span>
          </>}
        {/* aria-current="page": the last crumb IS the current location, and a
            screen reader should say so rather than read two names in a row. */}
        <b aria-current="page" {...stylex.props(styles.crumbStrong)}>
          {page}
        </b>
      </nav>

      <div {...stylex.props(styles.spacer)} />

      <div {...stylex.props(styles.searchActionsGroup)}>
        {onOpenSearch && <button type="button" onClick={onOpenSearch} aria-label={t('commandPaletteOpen')} title={t('commandPaletteOpen')} {...stylex.props(styles.searchTrigger)}>
            <SearchIcon size={16} />
            <span {...stylex.props(styles.searchTriggerText)}>{t('commandPalettePlaceholder')}</span>
            {searchShortcutLabel != null && <span aria-hidden="true" {...stylex.props(styles.searchKbd)}>
                {searchShortcutLabel}
              </span>}
          </button>}

        <div {...stylex.props(styles.hideCompact)}>{actions}</div>
      </div>

      {bell}

      <div {...stylex.props(styles.menuWrap, styles.hideCompact)}>
        <button type="button" onClick={lang.onTriggerClick} ref={langBtnRef} aria-label={t('switchLanguage')} aria-haspopup="menu" aria-expanded={lang.open} title={t('switchLanguage')} {...stylex.props(styles.iconBtn, styles.langBtn)}>
          <span {...stylex.props(styles.langCode)}>{locale.toUpperCase()}</span>
          <ChevronDownIcon size={13} {...stylex.props(styles.chev, lang.open && styles.chevOpen)} />
        </button>

        {lang.open && <div onClick={e => e.stopPropagation()} {...menuProps} className={menuCls}>
            {LANGUAGES.map(l => {
          const active = l.code === locale;
          return <button key={l.code} type="button" onClick={() => {
            setLocale(l.code);
            lang.close();
          }} {...stylex.props(styles.menuItem, active && styles.menuItemOn)}>
                  <span {...stylex.props(styles.menuItemLabel)}>{l.name}</span>
                  {active && <CheckIcon size={14} strokeWidth={2.5} {...stylex.props(styles.menuCheck)} />}
                </button>;
        })}
          </div>}
      </div>

      {onRefresh && <button type="button" onClick={onRefresh} disabled={refreshing} aria-label={t('refreshData')} title={t('refreshData')} {...stylex.props(styles.iconBtn, styles.hideCompact, refreshing && styles.iconBtnSpinning)}>
          <RefreshIcon size={18} />
        </button>}

      <button type="button" onClick={onThemeToggle} aria-label={theme === 'dark' ? t('switchToLight') : t('switchToDark')} title={theme === 'dark' ? t('switchToLight') : t('switchToDark')} {...stylex.props(styles.iconBtn, styles.hideCompact)}>
        {theme === 'dark' ? <SunIcon size={18} /> : <MoonIcon size={18} />}
      </button>

      {/* R-346 §21 — everything that folded, in one menu. Mirrors the redesign's
          "more options": SITE, LANGUAGE, then refresh and the theme toggle. */}
      <div {...stylex.props(styles.menuWrap, styles.showCompact)}>
        <button type="button" ref={moreBtnRef} aria-label={t('moreOptions')} aria-haspopup="menu" aria-expanded={more.open} title={t('moreOptions')} onClick={more.onTriggerClick} {...stylex.props(styles.iconBtn)}>
          <MoreHorizontalIcon size={18} />
        </button>

        {more.open && <div onClick={e => e.stopPropagation()} {...wideProps} className={wideCls}>
            {actions != null && <>
                <div {...stylex.props(styles.menuSectionLabel)}>{t('site')}</div>
                {/* The site switcher is a SLOT — the bar does not know what is in
                    it, so it is rendered as-is rather than rebuilt here. */}
                <div {...stylex.props(styles.menuSlot)}>{actions}</div>
              </>}

            <div {...stylex.props(styles.menuSectionLabel)}>{t('language')}</div>
            {LANGUAGES.map(l => {
          const active = l.code === locale;
          return <button key={l.code} type="button" onClick={() => {
            setLocale(l.code);
            more.close();
          }} {...stylex.props(styles.menuItem, active && styles.menuItemOn)}>
                  <span {...stylex.props(styles.menuItemLabel)}>{l.name}</span>
                  {active && <CheckIcon size={14} strokeWidth={2.5} {...stylex.props(styles.menuCheck)} />}
                </button>;
        })}

            <div {...stylex.props(styles.menuDivider)} />

            {onRefresh && <button type="button" disabled={refreshing} onClick={() => {
          more.close();
          onRefresh();
        }} {...stylex.props(styles.menuItem)}>
                <RefreshIcon size={16} />
                <span {...stylex.props(styles.menuItemLabel)}>{t('refreshData')}</span>
              </button>}
            <button type="button" onClick={() => {
          more.close();
          onThemeToggle();
        }} {...stylex.props(styles.menuItem)}>
              {theme === 'dark' ? <SunIcon size={16} /> : <MoonIcon size={16} />}
              <span {...stylex.props(styles.menuItemLabel)}>
                {theme === 'dark' ? t('switchToLight') : t('switchToDark')}
              </span>
            </button>
          </div>}
      </div>

      {user && <div onKeyDown={e => {
      if (e.key === 'Escape' && userMenu.open) {
        userMenu.close();
        userBtnRef.current?.focus();
      }
    }} {...stylex.props(styles.menuWrap)}>
          <button type="button" title={user.name} ref={userBtnRef} aria-haspopup="menu" aria-expanded={userMenu.open} aria-label={user.name} onClick={userMenu.onTriggerClick} {...stylex.props(styles.iconBtn, styles.avatarBtn)}>
            <span {...stylex.props(styles.avatar)}>{user.initials}</span>
          </button>

          {userMenu.open && <div onClick={e => e.stopPropagation()} {...menuProps} className={menuCls}>
              <div {...stylex.props(styles.menuHeader)}>
                <div {...stylex.props(styles.menuUserName)}>{user.name}</div>
                <div {...stylex.props(styles.menuUserRole)}>{user.role}</div>
              </div>
              {accounts && accounts.length > 1 && onSwitchAccount && <>
                  <div {...stylex.props(styles.menuSection)}>{t('accounts')}</div>
                  {accounts.map(a => <div key={a.id} {...stylex.props(styles.accountRow)}>
                      <button type="button" disabled={a.active} aria-current={a.active} title={a.active ? a.name : `${t('switchAccount')} — ${a.name}`} onClick={() => {
              userMenu.close();
              onSwitchAccount(a.id);
            }} {...stylex.props(styles.menuItem, styles.accountPick, a.active && styles.menuItemOn)}>
                        <span {...stylex.props(styles.accountAvatar)}>{a.initials}</span>
                        <span {...stylex.props(styles.accountLines)}>
                          <span {...stylex.props(styles.accountName)}>{a.name}</span>
                          <span {...stylex.props(styles.accountRole)}>({a.role})</span>
                        </span>
                        {a.active && <CheckIcon size={14} strokeWidth={2.5} {...stylex.props(styles.menuCheck)} />}
                      </button>
                      {onSignOutAccount && <button type="button" aria-label={`${t('signOutOfAccount')} — ${a.name}`} title={t('signOutOfAccount')} onClick={() => {
              userMenu.close();
              onSignOutAccount(a.id);
            }} {...stylex.props(styles.accountSignOut)}>
                          <LogoutIcon size={14} />
                        </button>}
                    </div>)}
                  <div {...stylex.props(styles.menuDivider)} />
                </>}

              {onAddAccount && <button type="button" onClick={() => {
          userMenu.close();
          onAddAccount();
        }} {...stylex.props(styles.menuItem)}>
                  <PlusIcon size={16} />
                  <span {...stylex.props(styles.menuItemLabel)}>{t('addAccount')}</span>
                </button>}

              <a href={settingsHref} onClick={() => userMenu.close()} {...stylex.props(styles.menuItem)}>
                <SettingsIcon size={16} />
                <span {...stylex.props(styles.menuItemLabel)}>{t('settings')}</span>
              </a>
              <button type="button" onClick={() => {
          userMenu.close();
          onLogout?.();
        }} {...stylex.props(styles.menuItem)}>
                <LogoutIcon size={16} />
                <span {...stylex.props(styles.menuItemLabel)}>{t('logOut')}</span>
              </button>
            </div>}
        </div>}
    </header>;
}